import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
	createFileRoute,
	Outlet,
	redirect,
	useNavigate,
	useParams,
} from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";
import { Avatar } from "@/components/base/avatar/avatar";
import { BadgeWithDot } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import { EmptyValue } from "@/components/common/EmptyValue";
import CopyableId from "@/components/Molecules/CopyableId/CopyableId";
import {
	UserOrgTableFilters,
	type UserOrgTableFiltersState,
} from "@/components/Molecules/FilterDrawer/UserOrgTableFilters";
import type { ColumnDef } from "@/components/Molecules/Table";
import { CustomTableWrapper } from "@/components/Molecules/Table/CustomTableWrapper";
import { TableSearchEmptyState } from "@/components/Molecules/Table/TableSearchEmptyState";
import { CreateOrgScopedUserModal } from "@/components/Organisms/CreateOrgScopedUserModal/CreateOrgScopedUserModal";
import { PageHeader } from "@/components/Organisms/PageHeader/PageHeader";
import { PageLayout } from "@/components/Organisms/PageLayout/PageLayout";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { useEntityActionAllowed } from "@/hooks/use-entity-action-allowed";
import { useDebounced } from "@/hooks/use-debounced";
import { useTableSettings } from "@/hooks/use-table-settings";
import { useUserAvatarSrc } from "@/hooks/use-user-avatar-src";
import type { OrganizationUserDisplay } from "@/services/organization";
import {
	organizationQueryOptions,
	useExportOrganizationUsers,
} from "@/services/organization";
import {
	buildFilterParams,
	type FilterConfig,
} from "@/utils/build-filter-params";
import { downloadBlob, generateExportFilename } from "@/utils/download";
import { getUserInitials } from "@/utils/get-user-initials";
import { formatLongDate } from "@/utils/utils";
import { ViewTransitionTypes, viewTransition } from "@/utils/view-transitions";
import usersPng from "/assets/users.png";
import { statusColors } from "./users/(org-users)/$orgUserId";

// Avatar cell component - extracted to properly use hooks
function UserAvatarCell({ user }: { user: OrganizationUserDisplay }) {
	const { src: userAvatarSrc, isLoading } = useUserAvatarSrc(
		user.userId,
		user.hasAvatar,
	);

	const firstName = user.firstName;
	const lastName = user.lastName;
	const email = user.email;

	// Only show initials when not loading and no avatar
	const initials =
		!isLoading && !userAvatarSrc
			? getUserInitials(firstName, lastName)
			: undefined;

	return (
		<div className="flex items-center gap-3">
			<Avatar
				src={userAvatarSrc || undefined}
				initials={initials}
				alt={user.displayName ?? undefined}
				size="md"
			/>
			<div className="whitespace-nowrap">
				<p className="text-sm font-medium text-primary">
					{user.displayName && user.displayName.length > 0 ? (
						user.displayName
					) : firstName &&
						firstName.length > 0 &&
						lastName &&
						lastName.length > 0 ? (
						`${firstName} ${lastName}`
					) : (
						<EmptyValue />
					)}
				</p>
				<p className="text-sm font-medium text-gray-600">
					{email && email.length > 0 ? email : <EmptyValue />}
				</p>
			</div>
		</div>
	);
}

// Table settings constants
const TABLE_KEY = "orgAdmin.users";
const DEFAULT_COLUMN_ORDER = [
	"nameEmail",
	"UserRoles.OrganizationRole.Name",
	"belongsTo",
	"status.name",
	"activated",
	"lastUpdated",
	"lastLoggedIn",
];
const DEFAULT_SORTING = [{ id: "nameEmail", desc: false }];
const DEFAULT_PAGE_SIZE = 10;

const normalizeColumnOrder = (columnOrder?: string[]) => {
	if (!Array.isArray(columnOrder) || columnOrder.length === 0) {
		return [...DEFAULT_COLUMN_ORDER];
	}

	const allowedColumns = new Set(DEFAULT_COLUMN_ORDER);
	const sanitized = columnOrder.filter((columnId) =>
		allowedColumns.has(columnId),
	);
	const unique = Array.from(new Set(sanitized));
	const missingDefaults = DEFAULT_COLUMN_ORDER.filter(
		(columnId) => !unique.includes(columnId),
	);

	return [...unique, ...missingDefaults];
};

// Serializable filter state for persistence (excludes date ranges)
interface PersistedFilters {
	roles?: string[];
	organizationIds?: string[];
	subOrganizationIds?: string[];
	subContractorIds?: string[];
	orgMemberStatus?: string[];
}
const DEFAULT_FILTERS: PersistedFilters = {
	roles: [],
	organizationIds: [],
	subOrganizationIds: [],
	subContractorIds: [],
	orgMemberStatus: [],
};

export const Route = createFileRoute(
	"/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/users",
)({
	component: RouteComponent,
	beforeLoad: ({ location }) => {
		if (
			location.pathname === "/admin/users" ||
			location.pathname === "/admin/users/"
		) {
			console.log("Redirecting to organization");
			throw redirect({
				to: "/admin/users/organization",
				replace: true,
				...viewTransition(ViewTransitionTypes.FADE),
			});
		}
	},
	validateSearch: (search) => {
		return z
			.object({
				create: z.enum(["organization"]).optional(),
				filterOpen: z.boolean().optional(),
			})
			.parse(search);
	},
});

function RouteComponent() {
	const navigate = useNavigate();
	const { t } = useAppTranslation();
	const queryClient = useQueryClient();
	const params = useParams({
		from: "/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/users",
	});
	const [pageSize, setPageSize] = useState(10);
	const [pageIndex, setPageIndex] = useState(0);
	const [searchTerm, setSearchTerm] = useState("");
	const [isRefreshing, setIsRefreshing] = useState(false);
	const [openOrganization, setOrganizationOpen] = useState(false);
	const canCreateOrganizationUser = useEntityActionAllowed({
		name: "CreateOrganizationUser",
	});
	const [tableOptionsHost, setTableOptionsHost] =
		useState<HTMLDivElement | null>(null);
	const [filters, setFilters] = useState<UserOrgTableFiltersState>({
		roles: [],
		organizationIds: [],
		subOrganizationIds: [],
		subContractorIds: [],
		orgMemberStatus: [],
		activated: null,
		lastUpdateDateRange: null,
		lastLoginDateRange: null,
	});
	const [sortParams, setSortParams] = useState({
		"OrderBy.Fields": ["User.LastName", "User.FirstName"],
		"OrderBy.Descending": false,
	});

	// Track if persisted settings have been applied
	const hasAppliedPersistedSettings = useRef(false);

	// Table settings persistence
	const {
		settings: tableSettings,
		isLoaded: settingsLoaded,
		saveSorting,
		saveColumnOrder,
		saveColumnVisibility,
		saveSearchTerm,
		saveFilters,
		savePageSize,
	} = useTableSettings<PersistedFilters>({
		tableKey: TABLE_KEY,
		defaultSorting: DEFAULT_SORTING,
		defaultColumnOrder: DEFAULT_COLUMN_ORDER,
		defaultSearchTerm: "",
		defaultFilters: DEFAULT_FILTERS,
		defaultPageSize: DEFAULT_PAGE_SIZE,
	});

	// Apply persisted settings (search, filters, page size) when config has loaded
	useEffect(() => {
		if (!settingsLoaded || hasAppliedPersistedSettings.current) return;
		hasAppliedPersistedSettings.current = true;

		if (tableSettings.searchTerm) {
			setSearchTerm(tableSettings.searchTerm);
		}
		if (tableSettings.filters) {
			setFilters((prev) => ({
				...prev,
				roles: tableSettings.filters.roles ?? [],
				organizationIds: tableSettings.filters.organizationIds ?? [],
				subOrganizationIds: tableSettings.filters.subOrganizationIds,
				subContractorIds: tableSettings.filters.subContractorIds,
				orgMemberStatus: tableSettings.filters.orgMemberStatus ?? [],
			}));
		}
		if (
			Number.isFinite(tableSettings.pageSize) &&
			[10, 25, 50, 100].includes(tableSettings.pageSize)
		) {
			setPageSize(tableSettings.pageSize);
		}
	}, [
		settingsLoaded,
		tableSettings.searchTerm,
		tableSettings.filters,
		tableSettings.pageSize,
	]);

	const sortFieldMapping: Record<string, string[]> = {
		nameEmail: ["User.LastName", "User.FirstName"],
		"UserRoles.OrganizationRole.Name": ["UserRoles.OrganizationRole.Name"],
		belongsTo: ["SubOrganization.Name", "SubContractor.Name"],
		"status.name": ["Status"],
		activated: ["ActivatedOn"],
		lastUpdated: ["UpdatedAt"],
		lastLoggedIn: ["LastLoggedInAt"],
		id: ["Id"],
	};
	const startIndex = pageIndex * pageSize + 1;
	const exportMutation = useExportOrganizationUsers(params.orgId);
	const debouncedFilters = useDebounced(filters, 500);

	const filterConfig: FilterConfig = {
		roles: { paramName: "OrganizationRoleIds" },
		organizationIds: { paramName: "OrganizationId", transform: "join" },
		subOrganizationIds: {
			paramName: "SubOrganizationIds",
		},
		subContractorIds: {
			paramName: "SubContractorIds",
		},
		orgMemberStatus: { paramName: "Statuses", transform: "join" },
		"activated.start": {
			paramName: "ActivatedAfter",
			transform: "dateStart",
		},
		"activated.end": {
			paramName: "ActivatedBefore",
			transform: "dateEnd",
			dateStartField: "activated.start",
		},
		"lastUpdateDateRange.start": {
			paramName: "UpdatedAfter",
			transform: "dateStart",
		},
		"lastUpdateDateRange.end": {
			paramName: "UpdatedBefore",
			transform: "dateEnd",
			dateStartField: "lastUpdateDateRange.start",
		},
		"lastLoginDateRange.start": {
			paramName: "LastLoggedInAfter",
			transform: "dateStart",
		},
		"lastLoginDateRange.end": {
			paramName: "LastLoggedInBefore",
			transform: "dateEnd",
			dateStartField: "lastLoginDateRange.start",
		},
	};

	const filterParams = useMemo(
		() => buildFilterParams(debouncedFilters, filterConfig),
		[debouncedFilters],
	);

	const _orgUserCount = useQuery(
		organizationQueryOptions.users.count(params.orgId, {
			SearchTerm: searchTerm,
			...filterParams,
			...sortParams,
		}),
	);

	const unFilteredOrgUserCount = useQuery(
		organizationQueryOptions.users.count(params.orgId),
	);

	const allUsers = useQuery(
		organizationQueryOptions.users.list(params.orgId, startIndex, pageSize, {
			SearchTerm: searchTerm,
			...filterParams,
			...sortParams,
		}),
	);

	const data = useMemo<OrganizationUserDisplay[]>(() => {
		return allUsers.data ?? [];
	}, [allUsers.data]);

	const pageCount = useMemo(() => {
		if (!_orgUserCount.data) return 0;
		return Math.ceil(_orgUserCount.data / pageSize);
	}, [_orgUserCount.data, pageSize]);

	const handleRefresh = async () => {
		setIsRefreshing(true);
		try {
			await allUsers.refetch();
		} finally {
			setIsRefreshing(false);
		}
	};

	const handleExport = (visibleColumns?: string[]) => {
		const totalCount = _orgUserCount.data ?? 0;
		if (totalCount === 0 || totalCount > 10000) return;

		const mappedColumns = visibleColumns?.flatMap((col) => {
			if (col === "nameEmail") {
				return ["firstName", "lastName", "email"];
			}
			return col;
		});

		exportMutation.mutate(
			{
				startIndex: 1,
				count: totalCount,
				params: {
					SearchTerm: searchTerm,
					...filterParams,
					...sortParams,
					columns: mappedColumns?.join(","),
				},
			},
			{
				onSuccess: (blob) => {
					downloadBlob(blob, generateExportFilename("organization_users"));
				},
			},
		);
	};

	const handleRowClick = (user: OrganizationUserDisplay) => {
		navigate({
			to: "/org-admin/$orgId/users/$orgUserId/$userId/details",
			params: { orgId: params.orgId, orgUserId: user.id, userId: user.userId },
			search: {
				mode: "view",
				tab: "details",
			},
		});
	};

	const handlePaginationChange = ({
		pageSize: newPageSize,
		pageIndex: newPageIndex,
	}: {
		pageSize: number;
		pageIndex: number;
	}) => {
		if (newPageSize !== pageSize) {
			setPageSize(newPageSize);
			setPageIndex(0);
			savePageSize(newPageSize);
		} else if (newPageIndex !== pageIndex) {
			setPageIndex(newPageIndex);
		}
	};

	const handleDebouncedSearchChange = (search: string) => {
		setSearchTerm(search);
		setPageIndex(0);
		// Save search term for persistence
		saveSearchTerm(search);
	};

	const handleFilterChange = (newFilters: UserOrgTableFiltersState) => {
		setFilters(newFilters);
		// Save filters for persistence (only serializable fields)
		saveFilters({
			roles: newFilters.roles,
			organizationIds: newFilters.organizationIds,
			subOrganizationIds: newFilters.subOrganizationIds,
			subContractorIds: newFilters.subContractorIds,
			orgMemberStatus: newFilters.orgMemberStatus,
		});
	};

	const userColumns: ColumnDef<OrganizationUserDisplay>[] = [
		{
			id: "nameEmail",
			accessorKey: "nameEmail",
			header: t("admin.users.tabs.table.columns.nameEmail"),
			enableSorting: true,
			meta: {
				headerProps: {
					isRowHeader: true,
				},
			},
			minSize: 300,
			cell: ({ row }) => <UserAvatarCell user={row.original} />,
		},
		{
			id: "UserRoles.OrganizationRole.Name",
			accessorKey: "UserRoles.OrganizationRole.Name",
			header: t("admin.users.tabs.table.columns.role"),
			minSize: 200,
			meta: {
				cellProps: {
					className: "max-w-[100px]",
				},
			},
			enableSorting: true,
			cell: ({ row }) => {
				const user = row.original;
				return (
					<>
						{user?.organizationRoles && user?.organizationRoles?.length > 0 ? (
							user?.organizationRoles?.map((role) => {
								const roles = role.name ?? "";
								return (
									<Tooltip key={role.id} title={roles} placement="top">
										<TooltipTrigger className="inline-flex min-w-0 max-w-full p-0 bg-transparent">
											<span className="text-sm font-medium truncate">
												{roles}
											</span>
										</TooltipTrigger>
									</Tooltip>
								);
							})
						) : (
							<EmptyValue />
						)}
					</>
				);
			},
		},
		{
			id: "belongsTo",
			accessorKey: "belongsTo",
			header: t("admin.users.tabs.table.columns.belongsTo"),
			minSize: 200,
			meta: {
				cellProps: {
					className: "max-w-[100px]",
				},
			},
			enableSorting: true,
			cell: ({ row }) => {
				const belongsTo = row.original.belongsTo;
				return belongsTo ? (
					<span className="text-sm font-medium truncate">{belongsTo}</span>
				) : (
					<EmptyValue />
				);
			},
		},
		{
			id: "status.name",
			accessorKey: "status.name",
			header: t("org-admin.users.orgMemberStatus"),
			enableSorting: true,
			minSize: 200,
			cell: ({ row }) => {
				const statusName = row.original.status.name ?? "";
				const color =
					statusColors[statusName as keyof typeof statusColors] ?? "gray";
				return statusName ? (
					<BadgeWithDot type="modern" size="md" color={color}>
						{statusName}
					</BadgeWithDot>
				) : (
					<EmptyValue />
				);
			},
		},
		{
			id: "activated",
			accessorKey: "activated",
			header: t("org-admin.users.activated"),
			enableSorting: true,
			minSize: 175,
			cell: ({ row }) => {
				return row.original.activated ? (
					<span className="text-sm font-medium">
						{row.original.activated && formatLongDate(row.original.activated)}
					</span>
				) : (
					<EmptyValue />
				);
			},
		},
		{
			id: "lastUpdated",
			accessorKey: "lastUpdated",
			header: t("admin.users.tabs.table.columns.lastUpdate"),
			enableSorting: true,
			minSize: 175,
			cell: ({ row }) => {
				return row.original.lastUpdated !== undefined ||
					row.original.lastUpdated !== null ? (
					<span className="text-sm font-medium">
						{row.original.lastUpdated &&
							formatLongDate(row.original.lastUpdated)}
					</span>
				) : (
					<EmptyValue />
				);
			},
		},
		{
			id: "lastLoggedIn",
			accessorKey: "lastLoggedIn",
			header: t("admin.users.tabs.table.columns.lastLogin"),
			enableSorting: true,
			minSize: 175,
			cell: ({ row }) => {
				return row.original.lastLoggedIn ? (
					<span className="text-sm font-medium">
						{row.original.lastLoggedIn &&
							formatLongDate(row.original.lastLoggedIn, {
								includeTime: true,
							})}
					</span>
				) : (
					<EmptyValue />
				);
			},
		},
		{
			id: "id",
			accessorKey: "id",
			header: t("admin.users.tabs.table.columns.id"),
			enableSorting: true,
			minSize: 200,
			cell: ({ row }) => {
				return row.original.id ? (
					<CopyableId value={row.original.id} />
				) : (
					<EmptyValue />
				);
			},
		},
	];

	const noOrganizationUsers = (isSearchEmpty: boolean) => {
		if (isSearchEmpty) {
			return (
				<TableSearchEmptyState titleKey="admin.users.tabs.empty.noSearchResultsTitle" />
			);
		}

		return (
			<div className="flex flex-col items-center justify-center">
				<div className="relative flex items-center justify-center w-[480px] h-[480px]">
					<div className="relative flex flex-col items-center justify-center">
						<img
							src={usersPng}
							alt={t("admin.users.tabs.empty.usersAlt")}
							className=" w-[152px] h-[118px] object-contain"
						/>
						<div className="mt-4 text-center">
							<h2 className="font-semibold text-primary text-md">
								{t("admin.users.tabs.empty.organizationTitle")}
							</h2>
							<p className="text-sm text-tertiary">
								{t("admin.users.tabs.empty.description")}
							</p>
						</div>
						{canCreateOrganizationUser.allowed ? (
							<Button
								size="sm"
								color="primary"
								data-testid="org-scoped-user-create-open-btn"
								onClick={() => {
									setOrganizationOpen(true);
									navigate({
										to: "/org-admin/$orgId/users",
										params: { orgId: params.orgId },
										search: { create: "organization" } as Record<
											string,
											unknown
										>,
									});
								}}
								className="mt-4"
							>
								{t("org-admin.users.createOrganizationUser.title")}
							</Button>
						) : null}
					</div>
				</div>
			</div>
		);
	};

	useEffect(() => {
		if (!pageCount) return;

		const prefetchRange = 1;

		for (let i = 1; i <= prefetchRange; i++) {
			const nextPageIndex = pageIndex + i;
			if (nextPageIndex < pageCount) {
				const nextStartIndex = nextPageIndex * pageSize + 1;
				queryClient.prefetchQuery(
					organizationQueryOptions.users.list(
						params.orgId,
						nextStartIndex,
						pageSize,
						{
							SearchTerm: searchTerm,
							...filterParams,
							...sortParams,
						},
					),
				);
			}
		}

		for (let i = 1; i <= prefetchRange; i++) {
			const prevPageIndex = pageIndex - i;
			if (prevPageIndex >= 0) {
				const prevStartIndex = prevPageIndex * pageSize + 1;
				queryClient.prefetchQuery(
					organizationQueryOptions.users.list(
						params.orgId,
						prevStartIndex,
						pageSize,
						{
							SearchTerm: searchTerm,
							...filterParams,
							...sortParams,
						},
					),
				);
			}
		}
	}, [
		pageIndex,
		pageSize,
		pageCount,
		searchTerm,
		filterParams,
		sortParams,
		queryClient,
		params.orgId,
	]);

	if (allUsers.isError) {
		return (
			<div className="space-y-0">
				<div className="flex flex-col items-center justify-center py-12">
					<div className="text-center">
						<h3 className="text-sm font-medium text-error">
							{t("admin.users.tabs.errors.failedToLoadUsers")}
						</h3>
						<p className="text-sm text-tertiary mt-1">
							{allUsers.error?.message}
						</p>
						<Button
							size="sm"
							color="primary"
							onClick={handleRefresh}
							className="mt-3"
						>
							{t("common.actions.tryAgain")}
						</Button>
					</div>
				</div>
			</div>
		);
	}

	return (
		<>
			<PageLayout
				header={
					<PageHeader
						type="landing"
						title={t("org-admin.users.title")}
						description={t("org-admin.users.description")}
						actions={
							data.length > 0 && canCreateOrganizationUser.allowed
								? [
										{
											label: t("org-admin.users.createOrganizationUser.title"),
											testId: "org-scoped-user-create-open-btn",
											color: "accent",
											onPress: () => {
												setOrganizationOpen(true);
												navigate({
													to: "/org-admin/$orgId/users",
													params: { orgId: params.orgId },
													search: { create: "organization" } as Record<
														string,
														unknown
													>,
												});
											},
										},
									]
								: undefined
						}
						overflowControl={<div ref={setTableOptionsHost} />}
						overflowAriaLabel={t("common.tableActions.options")}
					/>
				}
			>
				<CustomTableWrapper
					tableKey={TABLE_KEY}
					// Data
					data={data}
					columns={userColumns}
					// Display
					title={t("admin.users.tabs.table.title")}
					showHeaderPadding={false}
					zebraStriping
					// Loading States
					isLoading={allUsers.isLoading}
					isFetching={allUsers.isFetching}
					isRefreshing={isRefreshing}
					onRefresh={handleRefresh}
					// Pagination
					paginationMode="server"
					externalPaginationState={{ pageSize, pageIndex }}
					onPaginationChange={handlePaginationChange}
					pageCount={pageCount}
					rowCount={_orgUserCount.data}
					totalRowCount={unFilteredOrgUserCount.data}
					// Sorting
					sortFieldMapping={sortFieldMapping}
					defaultSortField="nameEmail"
					onSortChange={({ fields, descending }) => {
						setSortParams({
							"OrderBy.Fields": fields,
							"OrderBy.Descending": descending,
						});
					}}
					// Filtering
					filterPlaceholder={t("org-admin.users.table.search.placeholder")}
					filterValue={searchTerm}
					onDebouncedFilterChange={handleDebouncedSearchChange}
					filtersTrailingContent={
						<UserOrgTableFilters
							value={filters}
							onChange={handleFilterChange}
							organizationId={params.orgId}
						/>
					}
					// Table Actions
					enableTableActions
					filterLayout="bar"
					filterBarType="landing"
					filterBarClassName="pt-0"
					tableOptionsPortalTarget={tableOptionsHost}
					hasActiveFilters={Object.keys(filterParams).length > 0}
					onExportClick={handleExport}
					isExportLoading={exportMutation.isPending}
					// Features
					enableColumnReordering
					onRowClick={handleRowClick}
					enableColumnResizing
					// Persistence - Initial values from saved settings
					initialSortingState={tableSettings.sorting}
					initialColumnOrder={normalizeColumnOrder(tableSettings.columnOrder)}
					initialColumnVisibility={tableSettings.columnVisibility}
					settingsLoaded={settingsLoaded}
					// Persistence - Save callbacks
					onSortingStateChange={saveSorting}
					onColumnOrderChange={(order) => {
						saveColumnOrder(normalizeColumnOrder(order));
					}}
					onColumnVisibilityChange={saveColumnVisibility}
				/>

				{!allUsers.isLoading && !allUsers.isFetching && data.length <= 0
					? noOrganizationUsers(
							(unFilteredOrgUserCount.data ?? 0) > 0 &&
								(_orgUserCount.data ?? 0) === 0,
						)
					: null}
			</PageLayout>

			{canCreateOrganizationUser.allowed ? (
			<CreateOrgScopedUserModal
				orgId={params.orgId}
				isOpen={openOrganization}
				setIsOpen={(o) => {
					setOrganizationOpen(o);
					if (!o)
						navigate({
							to: "/org-admin/$orgId/users",
							params: { orgId: params.orgId },
							search: { create: "organization" } as Record<string, unknown>,
						});
				}}
				handleClose={() => {
					setOrganizationOpen(false);
					navigate({
						to: "/org-admin/$orgId/users",
						params: { orgId: params.orgId },
					});
				}}
			/>
			) : null}
			<Outlet />
		</>
	);
}
